import { GuideMenu } from '@/components/GuideMenu';
import { createFileRoute } from '@tanstack/react-router';

const OverviewSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Thievery: Covert Operations & Espionage
    </h3>
    <h4 className="text-lg font-semibold text-[#f0d060]">Overview</h4>
    <p className="text-[#b8b8b8] mb-2">
      Whoever said that leadership must be honourable has never ruled a province
      in Mythea. Strength does not always come from brute force or arcane
      mastery—sometimes, the most powerful weapons are those wielded in the
      shadows.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      As the ruler of your province, you command a state-sanctioned paramilitary
      thievery guild. What cannot be won through military conquest or mystical
      power can often be seized through deception, sabotage, and treachery.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      But do not underestimate the importance of your thieves—if you neglect
      them, your enemies will ensure that you and your people suffer the
      consequences.
    </p>
  </div>
);

const SuccessSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Determining Success
    </h3>
    <p className="text-[#b8b8b8] mb-2">
      The effectiveness of your covert operations is measured by a key factor:
      Thieves Per Acre (TPA)—the number of thieves you maintain relative to your
      land.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      Success in operations is determined by the relative TPA between you and
      your target. A higher TPA than your enemy increases your chances of
      success.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      The damage inflicted depends on how many thieves you send. Sending too
      many thieves can lead to significant losses if they are caught. Sending
      too few thieves may result in minimal damage.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      As with all things in Mythea, luck plays a role—random factors may
      influence the outcome of an operation. Mastering the art of espionage
      requires finding the perfect balance between risk and reward.
    </p>
  </div>
);

const BuildingSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Building & Maintaining Your Guild
    </h3>
    <p className="text-[#b8b8b8] mb-2">
      Thieves are not mere foot soldiers—they are highly trained specialists,
      honed in the arts of deception and subterfuge. Keeping your guild strong
      is essential to both offence and defence.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      Recruitment – You may train 1 thief for 2 training credits. Ensure you
      continue training more thieves as your province grows, or risk weakening
      your defences.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      Stealth – Every operation consumes stealth, representing how well-hidden
      your spies remain. If your stealth drops below 10%, you will be unable to
      conduct operations. Stealth regenerates over time as your guild rests and
      refines its techniques.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      A thief seen is a thief caught—be careful how aggressively you operate. A
      well-maintained guild ensures that your province is both well-defended
      from enemy spies and capable of striking from the shadows when needed.
    </p>
  </div>
);

const RestrictionsSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Restrictions & Limits
    </h3>
    <p className="text-[#b8b8b8] mb-2">
      Thievery in Mythea is not without consequences. The Lords of Mythea
      enforce rules on when certain operations can be used.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      Some operations require Unfriendly or Hostile relations before they can be
      executed. Using these operations against a known enemy is more effective
      than using them indiscriminately.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      Hostile-category operations increase your province&apos;s renown and
      Honour. For more details on Kingdom Relations, refer to the dedicated
      section in the Guide.
    </p>
  </div>
);

const OperationsSection = () => {
  const OperationsTable = ({
    operations,
    title,
  }: {
    operations: any[];
    title: string;
  }) => (
    <div className="space-y-4">
      <h4 className="text-lg font-semibold text-[#f0d060]">{title}</h4>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left border-b border-white/10">
              <th className="p-2 w-32"></th>
              <th className="p-2 text-[#f0d060]">Name</th>
              <th className="p-2 text-[#f0d060]">Description</th>
              <th className="p-2 text-[#f0d060]">Difficulty</th>
              <th className="p-2 text-[#f0d060]">Availability</th>
            </tr>
          </thead>
          <tbody></tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      <h3 className="text-xl font-semibold text-[#e6c547]">
        The Thieves Toolbox
      </h3>
      <p className="text-[#b8b8b8] mb-4">
        Below, you will find a reference guide detailing each available
        operation and its effects. Any restrictions on their use will be listed
        under the corresponding operation.
      </p>
      <div className="space-y-12">
        <OperationsTable operations={[]} title="Intelligence Operations" />
        <OperationsTable operations={[]} title="Resource Theft Operations" />
        <OperationsTable operations={[]} title="Black Operations" />
      </div>
    </div>
  );
};

export const Route = createFileRoute('/thievery/')({
  component: ThieveryPage,
});

function ThieveryPage() {
  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="max-w-6xl mx-auto space-y-8">
        <GuideMenu currentPath="/thievery" />

        {/* Main content section */}
        <section className="glass rounded-2xl p-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <img
              src="/images/nav/thievery@2x.png"
              alt="Thievery"
              width={80}
              height={80}
              className="shrink-0"
            />
            <h2 className="text-2xl font-bold">Thievery</h2>
          </div>

          {/* Content sections */}
          <div className="space-y-12 text-[#b8b8b8] leading-relaxed">
            <OverviewSection />
            <SuccessSection />
            <BuildingSection />
            <RestrictionsSection />
            <OperationsSection />
          </div>
        </section>
      </div>
    </div>
  );
}
